// client/src/pages/AdminCouponList.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Box, Typography, Button, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip, Switch, Stack,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';

const AdminCouponList = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ code: '', discountType: 'percentage', discountValue: '', maxUses: '', expiresAt: '' });
  const [formError, setFormError] = useState('');
  const { token } = useContext(AuthContext);

  const fetchCoupons = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/coupons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch coupons.');
      setCoupons(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleToggleActive = async (coupon) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/coupons/${coupon.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active: !coupon.active }),
      });
      if (!response.ok) throw new Error('Failed to update coupon.');
      fetchCoupons();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/coupons/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete coupon.');
      fetchCoupons();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCreate = async () => {
    setFormError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/coupons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...formData,
          maxUses: formData.maxUses || null,
          expiresAt: formData.expiresAt || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create coupon.');
      setFormOpen(false);
      setFormData({ code: '', discountType: 'percentage', discountValue: '', maxUses: '', expiresAt: '' });
      fetchCoupons();
    } catch (err) {
      setFormError(err.message);
    }
  };

  if (loading) return <CircularProgress size={28} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Coupons</Typography>
        <Button variant="contained" onClick={() => setFormOpen(true)}>New Coupon</Button>
      </Stack>

      {coupons.length === 0 ? (
        <EmptyState
          icon={<LocalOfferOutlinedIcon />}
          title="No coupons yet"
          description="Create a coupon code to offer customers a discount at checkout."
          actionLabel="New Coupon"
          onAction={() => setFormOpen(true)}
        />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell><TableCell>Discount</TableCell><TableCell>Uses</TableCell>
                <TableCell>Expires</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((coupon) => (
                <TableRow key={coupon.id} hover>
                  <TableCell><Chip label={coupon.code} size="small" /></TableCell>
                  <TableCell>
                    {coupon.discount_type === 'percentage' ? `${Number(coupon.discount_value)}%` : formatCurrency(coupon.discount_value)}
                  </TableCell>
                  <TableCell>{coupon.times_used}{coupon.max_uses ? ` / ${coupon.max_uses}` : ''}</TableCell>
                  <TableCell>{coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell><Switch checked={Boolean(coupon.active)} onChange={() => handleToggleActive(coupon)} size="small" /></TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => handleDelete(coupon.id)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Coupon</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Code" fullWidth value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            />
            <TextField
              select label="Discount Type" fullWidth value={formData.discountType}
              onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
            >
              <MenuItem value="percentage">Percentage off</MenuItem>
              <MenuItem value="fixed">Fixed amount off</MenuItem>
            </TextField>
            <TextField
              label={formData.discountType === 'percentage' ? 'Discount (%)' : 'Discount (R)'}
              type="number" fullWidth value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
            />
            <TextField
              label="Max Uses (optional)" type="number" fullWidth value={formData.maxUses}
              onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
            />
            <TextField
              label="Expires On (optional)" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!formData.code || !formData.discountValue}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCouponList;
